import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../auth/AuthContext";
import { toast } from "sonner";
import { MapPin, Star, BadgeCheck, Camera, Shield, Zap, Home, Lock, Square, Clock, CheckCircle2, ArrowRight, AlertTriangle, CreditCard, ArrowLeft } from "lucide-react";
import DateTimePicker from "../components/DateTimePicker";
import PriceInsight from "../components/PriceInsight";
import { setReturnTo } from "../lib/returnTo";
import ListingImage from "../components/ListingImage";
import { PARKING_FALLBACK } from "../lib/parkingImages";
import {
  rangeOverlapsDateTimes,
  nowInTimeZone,
  formatTripDateLabel,
  formatTripTimeLabel,
  clientTimeZone,
} from "../lib/dateLocal";
import { computeParkingSummary } from "../lib/parkingPricing";
import { formatBookingStatus, bookingStatusDotClass } from "../lib/bookingStatus";
import { formatNumber, formatUsd } from "../lib/currencyDisplay";

const FAC_META = {
  camera: { icon: <Camera size={14} />, label: "Camera security" },
  security_24_7: { icon: <Shield size={14} />, label: "24/7 security" },
  ev_charging: { icon: <Zap size={14} />, label: "EV charging" },
  covered_roof: { icon: <Home size={14} />, label: "Covered roof" },
  gated: { icon: <Lock size={14} />, label: "Gated" },
  fenced: { icon: <Square size={14} />, label: "Fenced" },
};
const ALL_FACILITIES = ["camera", "security_24_7", "ev_charging", "covered_roof", "gated", "fenced"];

export default function ParkingDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [checkIn, setCheckIn] = useState(params.get("check_in") || "");
  const [checkOut, setCheckOut] = useState(params.get("check_out") || "");
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [bookedRanges, setBookedRanges] = useState([]);
  const [stripeConfigured, setStripeConfigured] = useState(false);
  const [paymentCancelled, setPaymentCancelled] = useState(params.get("stripe_cancel") === "1");

  const spotTz = p?.booking_timezone || clientTimeZone();
  const minCheckIn = useMemo(() => nowInTimeZone(spotTz), [spotTz]);
  const operatingOpen = p && !p.open_24_7 ? p.open_time : undefined;
  const operatingClose = p && !p.open_24_7 ? p.close_time : undefined;

  useEffect(() => {
    (async () => {
      try {
        const [pr, ar] = await Promise.all([
          axios.get(`${API}/parking/${id}`),
          axios.get(`${API}/parking/${id}/availability`).catch(() => ({ data: { booked_ranges: [] } })),
        ]);
        setP(pr.data);
        setBookedRanges(ar.data?.booked_ranges || []);
      } catch (e) {
        toast.error("Spot not found"); navigate("/parking");
      }
    })();
  }, [id, navigate]);

  useEffect(() => {
    axios.get(`${API}/payments/stripe/config`)
      .then((r) => setStripeConfigured(!!r.data?.configured))
      .catch(() => setStripeConfigured(false));
  }, []);

  const releaseParkingHold = useCallback(() => {
    if (!user || !id) return;
    axios.post(
      `${API}/payments/stripe/release-parking-hold`,
      { spot_id: id },
      { withCredentials: true },
    ).catch(() => {});
  }, [user, id]);

  useEffect(() => {
    releaseParkingHold();
  }, [releaseParkingHold]);

  useEffect(() => {
    const onPageShow = (e) => {
      if (e.persisted) releaseParkingHold();
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, [releaseParkingHold]);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId || params.get("stripe_cancel") === "1") return;
    navigate(`/parking/${id}/complete?session_id=${encodeURIComponent(sessionId)}`, { replace: true });
  }, [id, params, navigate]);

  useEffect(() => {
    if (params.get("stripe_cancel") !== "1") return;
    const sessionId = params.get("session_id");
    if (sessionId) {
      axios.post(`${API}/payments/stripe/cancel`, { session_id: sessionId }, { withCredentials: true }).catch(() => {});
    }
    setPaymentCancelled(true);
    const next = new URLSearchParams(params);
    next.delete("stripe_cancel");
    next.delete("session_id");
    setParams(next, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const conflict = useMemo(() => {
    if (!checkIn || !checkOut) return false;
    return rangeOverlapsDateTimes(checkIn, checkOut, bookedRanges);
  }, [checkIn, checkOut, bookedRanges]);

  const summary = useMemo(() => {
    if (!p || !checkIn || !checkOut) return null;
    return computeParkingSummary(p, checkIn, checkOut);
  }, [p, checkIn, checkOut]);

  const completeMockBooking = async () => {
    const res = await axios.post(`${API}/parking-bookings`, {
      spot_id: id, check_in: checkIn, check_out: checkOut,
    }, { withCredentials: true });
    setConfirmed(res.data);
    toast.success("Parking reserved");
  };

  const reserve = async () => {
    if (!user) {
      setReturnTo(window.location.pathname + window.location.search);
      toast.message("Sign in to reserve");
      navigate("/login", { state: { authRequired: true } });
      return;
    }
    if (!checkIn || !checkOut) { toast.error("Pick check-in and check-out"); return; }
    if (conflict) { toast.error("These hours conflict with another booking — pick others"); return; }
    setSubmitting(true);
    try {
      const payload = { spot_id: id, check_in: checkIn, check_out: checkOut };
      const tryStripe = stripeConfigured && summary?.total >= 0.5;

      if (tryStripe) {
        try {
          const sr = await axios.post(`${API}/payments/stripe/parking-checkout-session`, payload, { withCredentials: true });
          if (sr.data?.url) {
            window.location.assign(sr.data.url);
            return;
          }
        } catch (stripeErr) {
          if (stripeErr.response?.status === 409 || stripeErr.response?.status === 400) throw stripeErr;
          toast.message("Stripe unavailable — completing with test card");
        }
      }

      await completeMockBooking();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 401) {
        setReturnTo(window.location.pathname + window.location.search);
        toast.message("Sign in to reserve");
        navigate("/login", { state: { authRequired: true } });
      } else if (e.response?.status === 409) {
        toast.error("Just got booked by someone else — refreshing availability");
        try {
          const ar = await axios.get(`${API}/parking/${id}/availability`);
          setBookedRanges(ar.data?.booked_ranges || []);
        } catch (_) {}
      } else {
        toast.error(detail || "Reservation failed");
      }
    } finally { setSubmitting(false); }
  };

  if (!p) return <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-20 text-[var(--zc-text-2)]">Loading…</div>;

  if (confirmed) {
    const confirmedCheckIn = confirmed.check_in || checkIn;
    const confirmedCheckOut = confirmed.check_out || checkOut;
    return (
      <div className="max-w-[820px] mx-auto px-4 sm:px-6 py-20" data-testid="parking-confirmed">
        <div className="zc-card p-6 sm:p-10 text-center">
          <div className="w-14 h-14 mx-auto grid place-items-center rounded-full" style={{ background: "rgba(22,163,74,0.12)" }}>
            <CheckCircle2 size={24} className="text-[var(--zc-success)]" />
          </div>
          <div className="eyebrow mt-6">Confirmed</div>
          <h1 className="zc-headline text-4xl mt-2">Parking reserved.</h1>
          <p className="text-[var(--zc-text-2)] mt-3 max-w-md mx-auto">{p.name} · {confirmed.hours}h</p>
          {(confirmedCheckIn || confirmedCheckOut) && (
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-md mx-auto text-left">
              {confirmedCheckIn && (
                <div className="zc-card p-4">
                  <div className="eyebrow mb-1">Check-in</div>
                  <div className="text-sm font-semibold text-[var(--zc-text)]">{formatTripDateLabel(confirmedCheckIn)}</div>
                  <div className="text-xs text-[var(--zc-text-2)] mt-0.5">{formatTripTimeLabel(confirmedCheckIn)}</div>
                </div>
              )}
              {confirmedCheckOut && (
                <div className="zc-card p-4">
                  <div className="eyebrow mb-1">Check-out</div>
                  <div className="text-sm font-semibold text-[var(--zc-text)]">{formatTripDateLabel(confirmedCheckOut)}</div>
                  <div className="text-xs text-[var(--zc-text-2)] mt-0.5">{formatTripTimeLabel(confirmedCheckOut)}</div>
                </div>
              )}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-10">
            <div className="zc-card p-4"><div className="eyebrow mb-1">Booking</div><div className="font-mono text-xs">{confirmed.booking_id}</div></div>
            <div className="zc-card p-4"><div className="eyebrow mb-1">Total</div><div className="zc-price text-2xl">${formatUsd(confirmed.total)}</div></div>
            <div className="zc-card p-4"><div className="eyebrow mb-1">Status</div><div className="text-sm flex items-center gap-2 mt-1 justify-center"><span className={"dot " + bookingStatusDotClass(confirmed.status)} /> {formatBookingStatus(confirmed)}</div></div>
          </div>
          <div className="flex justify-center gap-3 mt-10">
            <Link to="/profile/parking" className="px-5 h-11 grid place-items-center zc-btn-primary text-sm">My parking</Link>
            <Link to="/parking" className="px-5 h-11 grid place-items-center zc-btn-ghost text-sm">Find more</Link>
          </div>
        </div>
      </div>
    );
  }

  const payLabel = stripeConfigured && summary?.total >= 0.5
    ? `Pay $${formatUsd(summary.total)} with Stripe`
    : summary ? `Confirm & pay $${formatUsd(summary.total)}` : "Reserve Space";

  const reserveDisabled = submitting || !summary || conflict || summary?.exceedsMax;
  const reserveLabel = submitting
    ? "Processing…"
    : summary?.exceedsMax
      ? "Exceeds max stay"
      : conflict
        ? "Hours unavailable"
        : !user
          ? "Sign in to reserve"
          : payLabel;

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-12 pb-24 lg:pb-12" data-testid="parking-details-page">
      <div className="text-xs text-[var(--zc-text-3)] mb-6">
        Home <span className="mx-1.5">›</span> Parking <span className="mx-1.5">›</span>
        <span className="text-[var(--zc-text)]">{p.name}</span>
      </div>

      {paymentCancelled && (
        <div
          className="mb-6 p-4 rounded-lg border flex items-start gap-3"
          style={{ background: "rgba(245,158,11,0.1)", borderColor: "rgba(245,158,11,0.45)" }}
          data-testid="pd-stripe-cancel-banner"
        >
          <AlertTriangle size={18} className="text-[var(--zc-warn)] shrink-0 mt-0.5" />
          <div className="min-w-0">
            <div className="font-semibold text-[var(--zc-text)]">Payment cancelled — adjust your times below</div>
            <p className="text-sm text-[var(--zc-text-2)] mt-1">
              Your spot hold was released. Update check-in and check-out, then try payment again — or use the back button to pick a different window.
            </p>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-[1fr,420px] gap-10">
        <div>
          <div className="eyebrow mb-2">{p.space_type?.replace("_", " ")}</div>
          <h1 className="zc-headline text-4xl md:text-5xl tracking-tight" data-testid="pd-title">{p.name}</h1>
          <div className="flex flex-wrap items-center gap-5 text-sm text-[var(--zc-text-2)] mt-3">
            <div className="flex items-center gap-1"><MapPin size={12} /> {p.address}, {p.city}</div>
            {p.avg_rating > 0 && (
              <div className="flex items-center gap-1"><Star size={12} className="text-[var(--zc-accent)]" fill="currentColor" /><span className="text-[var(--zc-text)] font-semibold">{formatNumber(p.avg_rating, 1)}</span> <span className="text-[var(--zc-text-3)]">({p.rating_count})</span></div>
            )}
          </div>

          <div className="mt-8">
            <div className="aspect-[16/9] zc-card overflow-hidden" data-testid="pd-image">
              <ListingImage images={p?.images} fallback={PARKING_FALLBACK} alt={p.name} className="w-full h-full object-cover" />
            </div>
          </div>

          <div className="mt-10 zc-card p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-[var(--zc-accent-soft)] grid place-items-center">
                <BadgeCheck size={20} className="text-[var(--zc-accent)]" />
              </div>
              <div>
                <div className="text-xs text-[var(--zc-text-3)]">Hosted by</div>
                <div className="text-sm font-semibold">{p.host_name}</div>
                <div className="text-caption text-[var(--zc-success)] flex items-center gap-1 mt-0.5"><BadgeCheck size={10} /> Z-Cars verified host</div>
              </div>
            </div>
          </div>

          <div className="mt-10">
            <div className="eyebrow mb-3">Description</div>
            <p className="text-[var(--zc-text)] leading-relaxed max-w-2xl">{p.description}</p>
          </div>

          <div className="mt-10">
            <div className="eyebrow mb-3">Hours</div>
            <div className="text-sm flex items-center gap-2"><Clock size={14} className="text-[var(--zc-text-3)]" />
              {p.open_24_7 ? "Open 24/7" : `Open ${p.open_time} – ${p.close_time}`}
            </div>
            {p.open_24_7 && (!p.max_hours || Number(p.max_hours) >= 24) && (
              <p className="text-caption text-[var(--zc-success)] mt-2" data-testid="pd-24h-available">24-hour parking available</p>
            )}
            {p.max_hours != null && (
              <p className="text-sm text-[var(--zc-text-2)] mt-2" data-testid="pd-max-hours">
                Max stay: {p.max_hours} hour{p.max_hours === 1 ? "" : "s"} per booking
              </p>
            )}
          </div>

          <div className="mt-10">
            <div className="eyebrow mb-3">Amenities</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {ALL_FACILITIES.map(f => {
                const m = FAC_META[f]; const has = (p.facilities || []).includes(f);
                return (
                  <div key={f} className={"zc-card p-4 flex items-center gap-3 " + (has ? "border-2 border-[var(--zc-accent-soft)]" : "opacity-50")}>
                    <div className={"w-9 h-9 grid place-items-center rounded-lg " + (has ? "bg-[var(--zc-accent-soft)] text-[var(--zc-accent)]" : "bg-[var(--zc-bg-3)] text-[var(--zc-text-3)]")}>{m.icon}</div>
                    <div className="text-sm">{m.label}</div>
                    {has && <CheckCircle2 size={14} className="ml-auto text-[var(--zc-success)]" />}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <aside className="lg:sticky lg:top-24 h-fit zc-card p-6">
          <div className="flex items-baseline justify-between">
            <div>
              <div className="zc-price text-4xl">${formatUsd(p.price_per_hour)}</div>
              <div className="eyebrow mt-1">per hour</div>
            </div>
          </div>

          <div className="space-y-3 mt-5">
            <DateTimePicker
              label="Check-in"
              value={checkIn}
              onChange={setCheckIn}
              minDate={minCheckIn}
              timeZone={spotTz}
              operatingOpen={operatingOpen}
              operatingClose={operatingClose}
              bookedRanges={bookedRanges}
              testid="pd-checkin"
              helperText="Greyed days are already booked. Times are in the spot's local timezone."
            />
            <DateTimePicker
              label="Check-out"
              value={checkOut}
              onChange={setCheckOut}
              minDate={checkIn || minCheckIn}
              timeZone={spotTz}
              operatingOpen={operatingOpen}
              operatingClose={operatingClose}
              bookedRanges={bookedRanges}
              testid="pd-checkout"
              helperText={checkIn ? "Must be after check-in." : "Pick after check-in."}
            />
          </div>

          {summary?.exceedsMax && (
            <div className="mt-3 p-3 rounded-lg flex items-center gap-2 text-sm" style={{ background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", color: "var(--zc-danger)" }} data-testid="pd-max-hours-banner">
              <AlertTriangle size={14} /> This spot allows a maximum of {summary.maxHours} hour{summary.maxHours === 1 ? "" : "s"} per booking. Shorten your window to continue.
            </div>
          )}

          {conflict && (
            <div className="mt-3 p-3 rounded-lg flex items-center gap-2 text-sm" style={{ background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", color: "var(--zc-danger)" }} data-testid="pd-conflict-banner">
              <AlertTriangle size={14} /> Not available for these hours. Greyed-out days in the picker are already booked.
            </div>
          )}

          <div className="mt-5">
            <PriceInsight kind="parking" itemId={id} unit="/hr" />
          </div>

          {summary && !summary.exceedsMax && (
            <div className="mt-5 pt-5 border-t border-[var(--zc-border)] space-y-2 text-sm" data-testid="pd-summary">
              <div className="flex justify-between text-[var(--zc-text-2)]">
                <span>${formatUsd(p.price_per_hour)} × {summary.hours} hr</span>
                <span className="text-[var(--zc-text)] font-semibold">${formatUsd(summary.subtotal)}</span>
              </div>
              <div className="flex justify-between text-[var(--zc-text-2)]">
                <span>Z-Cars service fee</span>
                <span>${formatUsd(summary.fee)}</span>
              </div>
              <div className="flex justify-between pt-2 border-t border-[var(--zc-border)] mt-2">
                <span className="font-semibold">Total</span>
                <span className="zc-price text-2xl">${formatUsd(summary.total)}</span>
              </div>
            </div>
          )}

          {!user && (
            <div className="mt-5 p-3 rounded-lg text-xs text-[var(--zc-text-2)] border border-[var(--zc-border)] bg-[var(--zc-bg-2)]">
              Sign in or create an account to reserve this driveway. Identity verification is not required for parking.
            </div>
          )}

          <button onClick={reserve} disabled={reserveDisabled} className="w-full h-12 zc-btn-primary text-sm mt-6 flex items-center justify-center gap-2 disabled:opacity-60" data-testid="pd-reserve-cta">
            {submitting ? "Processing…" : summary?.exceedsMax ? "Exceeds max stay" : conflict ? "Hours unavailable" : !user ? "Sign in to reserve" : stripeConfigured && summary?.total >= 0.5 ? <><CreditCard size={14} /> {payLabel}</> : <>{payLabel} <ArrowRight size={14} /></>}
          </button>
          {paymentCancelled && (
            <button
              type="button"
              onClick={() => navigate("/parking")}
              className="w-full h-11 zc-btn-ghost text-sm mt-3 flex items-center justify-center gap-2"
              data-testid="pd-cancel-back"
            >
              <ArrowLeft size={14} /> Back to search
            </button>
          )}
          <div className="mt-4 zc-hint">
            {stripeConfigured && summary?.total >= 0.5
              ? "You'll complete payment securely via Stripe Checkout."
              : "Test mode: mock card when Stripe is not configured."}
          </div>
        </aside>
      </div>

      <div
        className="fixed bottom-0 inset-x-0 z-40 border-t border-[var(--zc-border)] bg-[var(--zc-bg)] px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] lg:hidden"
        data-testid="pd-mobile-sticky-bar"
      >
        <div className="flex items-center gap-3 max-w-[1400px] mx-auto">
          <div className="min-w-0 flex-1">
            <div className="eyebrow">Total</div>
            <div className="zc-price text-xl">
              {summary && !summary.exceedsMax ? `$${formatUsd(summary.total)}` : "—"}
            </div>
          </div>
          <button
            type="button"
            onClick={reserve}
            disabled={reserveDisabled}
            className="shrink-0 min-h-11 px-5 zc-btn-primary text-sm flex items-center justify-center gap-1.5 disabled:opacity-60"
            data-testid="pd-mobile-reserve-cta"
          >
            {reserveLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
